import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../Config';
import { 
    Play, Clock, Activity, Search, Edit2, Save, X, RotateCcw, AlertTriangle, FileText, CheckCircle, 
    GripHorizontal, AlertCircle, RefreshCw, Edit3,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import Swal from 'sweetalert2';
import LogProcesos from './LogProcesos';
import LogDetalleProcesos from './LogDetalleProcesos';

const ProcesosList = () => {
    const [procesos, setProcesos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [processing, setProcessing] = useState({}); // Track processing state per process
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [goToPage, setGoToPage] = useState('1');

    // Modal & Logs State
    const [selectedProcesoLogs, setSelectedProcesoLogs] = useState(null);
    const [selectedProcesoDetalle, setSelectedProcesoDetalle] = useState(null);
    const [editingProceso, setEditingProceso] = useState(null); // If not null, modal is open

    useEffect(() => {
        fetchProcesos();
    }, []);

    const fetchProcesos = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/procesos`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setProcesos(data);
            } else {
                setError('No se pudo obtener la lista de procesos. Verifique su conexión o permisos.');
            }
        } catch (error) {
            console.error('Error fetching processes:', error);
            setError('Error de conexión con el servidor.');
        } finally {
            setLoading(false);
        }
    };

    const handleEjecutar = async (codigo) => {
        try {
            // 1. Fetch Preview Count
            const token = localStorage.getItem('token');
            const qtyResponse = await fetch(`${API_BASE_URL}/procesos/${codigo}/cantidad`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const rawData = await qtyResponse.json();
            const cantidad = typeof rawData === 'object' ? (rawData.cantidad || 0) : rawData;

            // 2. Confirm Execution (General)
            const result = await Swal.fire({
                title: 'Confirmar Ejecución',
                html: `
                    <div class="p-2 text-left">
                        <p class="text-gray-600 mb-4">El proceso <span class="font-bold text-blue-600">${codigo}</span> está listo para ser lanzado.</p>
                        
                        <div class="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4 text-center">
                            <span class="text-3xl font-bold text-blue-700">${cantidad}</span>
                            <p class="text-xs uppercase tracking-wider text-blue-600 font-semibold mt-1">Socios pendientes</p>
                        </div>
                        <p class="text-sm text-gray-400 italic text-center">Esta acción generará obligaciones financieras para el periodo actual.</p>
                    </div>
                `,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, ejecutar ahora',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#3b82f6',
                reverseButtons: true
            });

            if (!result.isConfirmed) return;

            setProcessing(prev => ({ ...prev, [codigo]: true }));
            
            // 3. Execute
            const response = await fetch(`${API_BASE_URL}/procesos/${codigo}/ejecutar`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const resumen = await response.text();
                Swal.fire({
                    title: '¡Ejecución Exitosa!',
                    html: `
                        <div class="bg-green-50 text-green-700 p-4 rounded-lg border border-green-100">
                            <p class="font-bold mb-1">Resumen del procesamiento:</p>
                            <p class="text-sm">${resumen}</p>
                        </div>
                    `,
                    icon: 'success',
                    confirmButtonColor: '#10b981'
                });
                fetchProcesos();
            } else {
                const msg = await response.text();
                Swal.fire('Error en ejecución', msg, 'error');
            }
        } catch (error) {
            console.error('Execution error:', error);
            Swal.fire('Error', 'No se pudo completar la operación.', 'error');
        } finally {
            setProcessing(prev => ({ ...prev, [codigo]: false }));
        }
    };

    const handleVerErrores = async (codigo) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/procesos/errores?accion=${codigo}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const errores = await response.json();

            if (errores.length === 0) {
                Swal.fire({
                    title: 'Sin errores',
                    text: 'No se encontraron errores registrados para este proceso.',
                    icon: 'info',
                    confirmButtonColor: '#3b82f6'
                });
                return;
            }

            Swal.fire({
                title: `Errores de ${codigo}`,
                html: `
                    <div class="max-h-96 overflow-y-auto">
                        <table class="w-full text-xs text-left border-collapse">
                            <thead>
                                <tr class="bg-gray-100 uppercase text-gray-500">
                                    <th class="p-2 border">Socio ID</th>
                                    <th class="p-2 border">Motivo del Error</th>
                                    <th class="p-2 border text-center">Fecha</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${errores.map(e => `
                                    <tr class="hover:bg-red-50 transition-colors">
                                        <td class="p-2 border font-bold text-gray-700">#${e.referenciaId}</td>
                                        <td class="p-2 border text-red-600 font-medium">${e.mensajeUsuario}</td>
                                        <td class="p-2 border text-[10px] text-gray-400 text-center">${new Date(e.fechaRegistro).toLocaleString()}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </div>
                `,
                width: '800px',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#64748b'
            });
        } catch (error) {
            Swal.fire('Error', 'No se pudieron cargar los errores.', 'error');
        }
    };

    const handleSaveConfig = async (e) => {
        e.preventDefault();
        if (!editingProceso) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/procesos/${editingProceso.codigo}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    cronExpression: editingProceso.cronExpression,
                    activo: editingProceso.activo,
                    // descripcion: editingProceso.descripcion // Backend doesn't update description currently, but we send what matches entity
                })
            });

            if (response.ok) {
                Swal.fire('Actualizado', 'La configuración ha sido guardada.', 'success');
                setEditingProceso(null);
                fetchProcesos();
            } else {
                Swal.fire('Error', 'No se pudo guardar la configuración', 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'Error de conexión', 'error');
        }
    };

    const handleVerDetalles = (codigo) => {
        setSelectedProcesoDetalle(codigo);
    };

    const filteredProcesos = procesos.filter(p =>
        p.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.descripcion.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic
    const totalItems = filteredProcesos.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
    const currentItems = filteredProcesos.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1);
        setGoToPage('1');
    }, [searchTerm, rowsPerPage]);

    const handlePageChange = (newPage) => {
        const page = Math.max(1, Math.min(newPage, totalPages));
        setCurrentPage(page);
        setGoToPage(page.toString());
    };

    const handleGoToPage = (e) => {
        if (e.key === 'Enter') {
            const page = parseInt(goToPage);
            if (!isNaN(page)) {
                handlePageChange(page);
            }
        }
    };

    const formatCronToTime = (cron) => {
        if (!cron) return 'No definida';
        const parts = cron.split(' ');
        if (parts.length < 3) return cron;
        // Simple formatter for daily crons: "0 0 0 * * ?" -> "00:00:00"
        const sec = parts[0].padStart(2, '0');
        const min = parts[1].padStart(2, '0');
        const hour = parts[2].padStart(2, '0');
        return `${hour}:${min}:${sec}`;
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen font-sans">
            {/* Header Area */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                        Monitor de Procesos
                    </h1>
                    <p className="text-slate-500 font-medium">Gestión centralizada de tareas programadas</p>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="relative w-full md:w-64">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                        <input
                            type="text"
                            placeholder="Buscar proceso..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={fetchProcesos}
                        className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
                        title="Refrescar Lista"
                    >
                        <RotateCcw size={18} />
                    </button>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white border border-slate-200 shadow-sm overflow-hidden rounded-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50/50">
                                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider w-32 text-center">Estado</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider">Proceso / Tarea</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider w-40">Hora Programada</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider w-48">Última Ejecución</th>
                                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-wider w-64 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-500">
                                        <div className="flex justify-center items-center gap-2">
                                            <RotateCcw className="animate-spin" size={16} /> Cargando procesos...
                                        </div>
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-red-500 bg-red-50">
                                        <div className="flex flex-col items-center gap-2">
                                            <AlertTriangle size={24} />
                                            <span className="font-bold">{error}</span>
                                            <button 
                                                onClick={fetchProcesos}
                                                className="mt-2 text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition-colors"
                                            >
                                                Reintentar
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredProcesos.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-500">No se encontraron procesos activos.</td>
                                </tr>
                            ) : (
                                currentItems.map((proc, index) => (
                                    <tr key={proc.codigo} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex justify-center">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide shadow-sm ${
                                                    proc.activo 
                                                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                                                    : 'bg-slate-100 text-slate-500 border border-slate-200'
                                                }`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full ${proc.activo ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></span>
                                                    {proc.activo ? 'Escuchando' : 'Detenido'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-slate-900 text-sm">{proc.codigo}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">{proc.descripcion}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="inline-flex items-center gap-1.5 text-slate-700 text-xs font-bold font-mono">
                                                <Clock size={12} className="text-blue-500" /> {formatCronToTime(proc.cronExpression)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-[11px] text-slate-600 font-medium">
                                                {proc.ultimaEjecucion ? new Date(proc.ultimaEjecucion).toLocaleString() : <span className="text-slate-400 italic">Nunca ejecutado</span>}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end items-center gap-2">
                                                <button
                                                    onClick={() => handleEjecutar(proc.codigo)}
                                                    disabled={processing[proc.codigo]}
                                                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all flex items-center gap-1.5
                                                        ${processing[proc.codigo] 
                                                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                                                          : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md'
                                                        }`}
                                                >
                                                    {processing[proc.codigo] ? <RotateCcw className="animate-spin" size={12} /> : <Play size={10} fill="currentColor" />}
                                                    {processing[proc.codigo] ? 'Procesando...' : 'Ejecutar Ahora'}
                                                </button>
                                                
                                                <div className="h-6 w-[1px] bg-slate-200 mx-1"></div>

                                                <button
                                                    onClick={() => setSelectedProcesoLogs(proc)}
                                                    className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                                                    title="Ver Historial"
                                                >
                                                    <FileText size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleVerErrores(proc.codigo)}
                                                    className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                    title="Ver Errores"
                                                >
                                                    <AlertCircle size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleVerDetalles(proc.codigo)}
                                                    className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                                    title="Ver Detalles de Procesamiento"
                                                >
                                                    <Search size={16} />
                                                </button>
                                                <button
                                                    onClick={() => setEditingProceso(proc)}
                                                    className="p-2 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                                    title="Editar Horario"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Paginación estilo Premium */}
                <div className="p-4 bg-white border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Ir a</span>
                            <input 
                                type="text" 
                                className="w-12 h-8 border border-slate-200 rounded text-center text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500"
                                value={goToPage}
                                onChange={(e) => setGoToPage(e.target.value)}
                                onKeyDown={handleGoToPage}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Ver</span>
                            <select 
                                className="h-8 border border-slate-200 rounded text-xs font-bold px-2 outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                {[5, 10, 20, 50, 100].map(val => (
                                    <option key={val} value={val}>{val}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                        <button 
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                            <ChevronsLeft className="w-4 h-4" />
                        </button>
                        <button 
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === 1 ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>

                        <div className="flex items-center mx-2 gap-1">
                            {[...Array(totalPages)].map((_, i) => {
                                const p = i + 1;
                                if (totalPages > 5) {
                                    if (p !== 1 && p !== totalPages && Math.abs(p - currentPage) > 1) {
                                        if (p === 2 || p === totalPages - 1) return <span key={p} className="text-slate-300">...</span>;
                                        return null;
                                    }
                                }
                                return (
                                    <button
                                        key={p}
                                        onClick={() => handlePageChange(p)}
                                        className={`w-8 h-8 rounded text-xs font-bold transition-all ${currentPage === p ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-100'}`}
                                    >
                                        {p}
                                    </button>
                                );
                            })}
                        </div>

                        <button 
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button 
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className={`p-1.5 rounded hover:bg-slate-100 transition-colors ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                            <ChevronsRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        [{totalItems > 0 ? startIndex + 1 : 0} a {endIndex} de {totalItems}]
                    </div>
                </div>
            </div>

            {/* Edit Configuration Modal */}
            {editingProceso && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                            <h3 className="font-bold text-gray-800">Editar Proceso</h3>
                            <button onClick={() => setEditingProceso(null)} className="text-gray-400 hover:text-red-500"><X size={20} /></button>
                        </div>

                        <form onSubmit={handleSaveConfig} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Código del Proceso</label>
                                <input type="text" value={editingProceso.codigo} disabled className="w-full bg-gray-100 border border-gray-200 rounded p-2 text-gray-600 text-sm font-mono" />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Expresión Cron (Horario)</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        required
                                        className="flex-1 border border-gray-300 rounded p-2 text-sm font-mono focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={editingProceso.cronExpression}
                                        onChange={(e) => setEditingProceso({ ...editingProceso, cronExpression: e.target.value })}
                                    />
                                    <a href="https://crontab.guru/" target="_blank" rel="noreferrer" className="p-2 bg-gray-100 text-gray-600 rounded hover:bg-gray-200" title="Ayuda Cron">
                                        <AlertTriangle size={18} />
                                    </a>
                                </div>
                                <p className="text-xs text-gray-400 mt-1">Formato: Seg Min Hor Dia Mes Sem</p>
                            </div>

                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100 cursor-pointer" onClick={() => setEditingProceso({ ...editingProceso, activo: !editingProceso.activo })}>
                                <div className={`w-10 h-6 flex items-center rounded-full p-1 transition-colors ${editingProceso.activo ? 'bg-green-500' : 'bg-gray-300'}`}>
                                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${editingProceso.activo ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                </div>
                                <span className={`font-bold text-sm ${editingProceso.activo ? 'text-green-700' : 'text-gray-500'}`}>
                                    {editingProceso.activo ? 'PROCESO ACTIVO (Escuchando)' : 'PROCESO DETENIDO'}
                                </span>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button type="button" onClick={() => setEditingProceso(null)} className="flex-1 py-2 text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg font-medium text-sm">
                                    Cancelar
                                </button>
                                <button type="submit" className="flex-1 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium text-sm flex justify-center items-center gap-2">
                                    <Save size={16} /> Guardar Cambios
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Logs Modal */}
            {selectedProcesoLogs && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-5xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                        <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <FileText className="text-blue-600" size={20} />
                                Historial: {selectedProcesoLogs.descripcion}
                            </h2>
                            <button onClick={() => setSelectedProcesoLogs(null)} className="text-gray-400 hover:text-red-500"><X size={24} /></button>
                        </div>
                        <div className="flex-1 overflow-auto p-6 bg-white">
                            <LogProcesos codigo={selectedProcesoLogs.codigo} />
                        </div>
                    </div>
                </div>
            )}
            {/* Detailed Bitacora Modal */}
            {selectedProcesoDetalle && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-6xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                        <div className="p-4 border-b flex justify-between items-center bg-slate-50">
                            <div>
                                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                    <Search className="text-emerald-600" size={20} />
                                    Bitácora Detallada: {selectedProcesoDetalle}
                                </h2>
                                <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Detalle ítem por ítem del procesamiento ejecutado</p>
                            </div>
                            <button 
                                onClick={() => setSelectedProcesoDetalle(null)} 
                                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <LogDetalleProcesos codigo={selectedProcesoDetalle} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProcesosList;
